/**
 * ч.1376 — «Журнал доходів і витрат» ФОП-єдинника (облік лише за журналом).
 * Сервер: `GET /income-journal`, ручні записи `/income-journal/entries`, позначка «не дохід»
 * `/income-journal/exclusion`. Суми сервер віддає десятковими рядками («10400.0000») — у
 * розрахунках лише через Number().
 */
import { api } from './client';

/** Сума з сервера: десятковий рядок (Decimal). */
export type Amount = string | number;

export type JournalSection = 'INCOME' | 'EXPENSE';
export type JournalIncomeKind = 'RECEIVED' | 'REFUND' | 'FREE_RECEIVED' | 'PAYABLE_EXPIRED';
export type JournalExpenseKind = 'GOODS' | 'SALARY' | 'ESV' | 'OTHER';
/** Автоматичні рядки мають ще EXCLUDED / EXCLUDED_REFUND («не дохід») і NOT_INCOME. */
export type JournalRowKind = JournalIncomeKind | JournalExpenseKind | 'EXCLUDED' | 'EXCLUDED_REFUND' | 'NOT_INCOME';
export type PaymentForm = 'CASH' | 'NONCASH' | 'IN_KIND';

export interface JournalRow {
  source: 'AUTO' | 'MANUAL';
  id: string | null;
  document_type: string | null;
  document_id: string | null;
  document_number: string | null;
  document_label: string;
  date: string;
  section: JournalSection;
  kind: JournalRowKind;
  payment_form: PaymentForm | null;
  amount: Amount;
  contractor_id: string | null;
  contractor_name: string | null;
  description: string;
  account: string | null;
  reason: string | null;
  excludable: boolean;
}

export type JournalTotals = Record<
  'received_cash' | 'received_noncash' | 'received_in_kind' | 'received' | 'refund' | 'net'
  | 'free_received' | 'payable_expired' | 'income' | 'excluded' | 'not_income'
  | 'goods' | 'salary' | 'esv' | 'other' | 'expenses', Amount>;

export interface IncomeJournal {
  organization: {
    id: string; income_journal_only: boolean; income_journal_since: string | null;
    tax_system: string; not_eligible: string | null;
  };
  period: { date_from: string; date_to: string };
  rows: JournalRow[];
  totals: JournalTotals;
  year: { from: string; to: string; income: Amount; limit: Amount | null };
  opening_document: { id: string; document_number: string; document_date: string; posted: boolean; amount: Amount } | null;
  /** ч.1379: аванси покупців, що лишились в обліку (у режимі журналу їх бути не має). */
  open_advances: Amount;
  reasons: Record<string, string>;
}

export interface JournalEntryIn {
  organization_id: string;
  document_date: string;
  section: JournalSection;
  kind: JournalIncomeKind | JournalExpenseKind;
  payment_form?: PaymentForm | null;
  amount: string;
  contractor_id?: string | null;
  description?: string;
}

export const INCOME_KIND_LABELS: Record<JournalIncomeKind, string> = {
  RECEIVED: 'Отримано (дохід)',
  REFUND: 'Повернуто покупцю',
  FREE_RECEIVED: 'Безоплатно отримане',
  PAYABLE_EXPIRED: 'Кредиторка з минулим строком давності',
};
export const EXPENSE_KIND_LABELS: Record<JournalExpenseKind, string> = {
  GOODS: 'Товари, роботи, послуги',
  SALARY: 'Оплата праці',
  ESV: 'Єдиний внесок',
  OTHER: 'Інші витрати',
};
export const PAYMENT_FORM_LABELS: Record<PaymentForm, string> = {
  CASH: 'готівка', NONCASH: 'безготівка', IN_KIND: 'натуральна',
};

export const incomeJournal = {
  get: (p: { organization_id: string; date_from: string; date_to: string }) =>
    api.get<IncomeJournal>('/income-journal', { params: p }).then((r) => r.data),
  createEntry: (body: JournalEntryIn) =>
    api.post<{ id: string }>('/income-journal/entries', body).then((r) => r.data),
  updateEntry: (id: string, body: Partial<Omit<JournalEntryIn, 'organization_id' | 'section'>>) =>
    api.patch<{ id: string }>(`/income-journal/entries/${id}`, body).then((r) => r.data),
  deleteEntry: (id: string) => api.delete(`/income-journal/entries/${id}`).then(() => undefined),
  /** ч.1379: застосувати журнал до документів, уже проведених з дати початку. */
  apply: (organization_id: string) =>
    api.post<{ stripped: number; reposted: number; closing_document_id: string | null }>(
      '/income-journal/apply', { organization_id }).then((r) => r.data),
  setExclusion: (body: { organization_id: string; document_type: string; document_id: string; reason: string | null }) =>
    api.post<{ changed: number }>('/income-journal/exclusion', body).then((r) => r.data),
};
